import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link, useOutletContext } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { decodeHtml } from "../../utils/decodeHtml";
import { currency_converter_inr } from "../../utils/currencyUtils";
import RevenueOutstandingDuration from "./components/RevenueOutstandingDuration";
import RevenueOutstandingClient from "./components/RevenueOutstandingClient";
import RevenueOutstandingMember from "./components/RevenueOutstandingMember";
import useSmartBack from "../../hooks/useSmartBack";

function Outstanding() {
  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const [expenseFlag, setExpenseFlag] = useState("without_expense");
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const handleBack = useSmartBack("/order/list");

  const [data, setData] = useState([]);
  const [options, setoptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(""); // e.g., "2025-2026"
  const id = user.id;
  const get_outstanding_data = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_API_BASE_URL}/invoices/get_outstanding_data/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            year: selectedYear, // Pass the year to the backend
            flag: expenseFlag,
          },
        },
      );

      const d = response.data;
      if (response.status == 200) {
        const current_outstanding = d?.outstanding_current_year?.[0]?.sum || 0;
        const prev_outstanding = d?.outstanding_prev_year?.[0]?.sum || 1;

        setData({
          ...d,
          current_outstanding,
          prev_outstanding,
        });
        setoptions(d.outstanding_invoice_items_list || []);
      }
    } catch (error) {
      setGlobalLoading(false);
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    get_outstanding_data();
  }, [id, selectedYear, expenseFlag]);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      size: 70,
      Cell: ({ row }) => (
        <Link
          to={`/order/view/${row.original.invoice_id}`}
        >
          {decodeHtml(row.original.name)}
        </Link>
      ),
    },
    {
      accessorKey: "role_name",
      header: "Role Name",
      size: 70,
      Cell: ({ row }) => (
        <Link to={`/role/view/${row.original.role_id}`}>
          {decodeHtml(row.original.role_name)}
        </Link>
      ),
    },
    {
      accessorKey: "company_name",
      header: "Clients",
      size: 70,
    },
    {
      accessorKey: "roleManager",
      header: "Role Manager",
      size: 70,
    },
    {
      accessorKey: "assignedTo",
      header: "Assigned To",
      size: 70,
    },
    {
      accessorKey: "bdBy",
      header: "BD By",
      size: 70,
    },
    {
      accessorKey: "entity",
      header: "Entity",
      size: 40,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 40,
    },
    {
      accessorKey: "invoice_number",
      header: "Invoice Number",
      size: 70,
    },
    {
      accessorKey: "raised_date",
      header: "Raised On",
      size: 70,
    },
    {
      accessorKey: "issue_date",
      header: "Issued On",
      size: 70,
    },
    {
      accessorKey: "due_date",
      header: "Due Date",
      size: 70,
    },
    {
      accessorKey: "issue_age_days",
      header: "Issue Age",
      size: 70,
    },
    {
      accessorKey: "due_age_days",
      header: "Due Age",
      size: 70,
    },
    {
      accessorKey: "major_industry",
      header: "Industry",
      size: 70,
    },
    {
      accessorKey: "region",
      header: "Region",
      size: 70,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      size: 70,
    },
    {
      accessorKey: "revenue",
      header: "Total(INR)",
      size: 70,
      Cell: ({ row }) => currency_converter_inr(row.original.revenue),
    },
  ];
  const financialYears = [
    "2026-2027",
    "2025-2026",
    "2024-2025",
    "2023-2024",
    "2022-2023",
  ];
  const exportToExcel = () => {
    const exportData = options.map((row) => ({
      Name: decodeHtml(row.name),
      "Role Name": decodeHtml(row.role_name),
      Client: row.company_name,
      "Assigned To": row.assignedTo,
      Entity: row.entity,
      Status: row.status,
      "Invoice Number": row.invoice_number,
      "Raised On": row.raised_date,
      "Issued On": row.issue_date,
      "Due Date": row.due_date,
      "Issue Age": row.issue_age_days,
      "Due Age": row.due_age_days,
      Industry: row.major_industry,
      Region: row.region,
      Amount: row.amount,
      "Total (INR)": row.revenue,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Outstanding Invoices"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      file,
      `OutstandingInvoices_${new Date().getTime()}.xlsx`
    );
  };

  return (
    <>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Outstanding Invoices</h2>
          </div>
          <div className="mb-3 d-flex align-items-center gap-2">
            <button className="btn btn-light btn-sm" onClick={handleBack}>
                <i className="fa-solid fa-arrow-left me-1"></i> Back to Orders
            </button>
            <select
              className="form-select"
              style={{ width: "170px" }}
              value={expenseFlag}
              onChange={(e) => setExpenseFlag(e.target.value)}
            >
              <option value="without_expense">Without Expense</option>
              <option value="with_expense">With Expense</option>
            </select>
            <select
              className="form-select"
              style={{ width: "111px" }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
             <option value="">Till Today</option> 
             {financialYears.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
              {/* Add more years as needed */}
            </select>
          </div>
        </div>
        <div className="card border-0">
          <div className="row">
            <div className="col-xxl-12 d-flex">
              <div className="row flex-fill">
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-2"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-purple mb-1 me-2">
                          <i className="fa-solid fa-money-bills"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">Total Outstanding</span>
                          <span>
                            {currency_converter_inr(
                              data?.outstanding_current_year?.[0]?.revenue,
                            )}
                          </span>
                        </div>
                      </h3>
                      <div className="d-flex align-items-center">
                        <span className="fw-10">
                          Previous Year:{" "}
                          {currency_converter_inr(
                            data?.outstanding_prev_year?.[0]?.sum,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill div-focus-border">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-3"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-pink mb-1 me-2">
                          <i className="fa-solid fa-file-invoice"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">Sent Invoices</span>
                          <span>
                            {data?.outstanding_current_year?.[0]?.invoiceCount}
                          </span>
                        </div>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-3"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-warning mb-1 me-2">
                          <i className="fa-solid fa-clock"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">India Invoices</span>
                          <span>
                            {" "}
                            {
                              data?.outstanding_current_year?.[0]
                                ?.india_invoice_count
                            }
                          </span>
                        </div>
                      </h3>
                      <div className="d-flex align-items-center">
                        <span className="fw-10">
                          Outstanding:{" "}
                          {currency_converter_inr(
                            data?.outstanding_current_year?.[0]?.india_revenue,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-3"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-secondary mb-1 me-2">
                          <i className="fa-solid fa-plane"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">
                            International Invoices
                          </span>
                          <span>
                            {
                              data?.outstanding_current_year?.[0]
                                ?.international_invoice_count
                            }
                          </span>
                        </div>
                      </h3>
                      <div className="d-flex align-items-center">
                        <span className="fw-10">
                          Outstanding:{" "}
                          {currency_converter_inr(
                            data?.outstanding_current_year?.[0]
                              ?.international_revenue,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 d-flex">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5 className="mb-2">Outstanding by Duration</h5>
                </div>
                <div className="card-body">
                  <RevenueOutstandingDuration
                    chart_data={data?.revenue_outstanding_duration}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-4 d-flex">
              <div className="card flex-fill" style={{ height: "400px" }}>
                <div className="card-header pb-2">
                  <h5 className="mb-0">Split by Client</h5>
                </div>
                <div
                  className="card-body d-flex flex-column p-2"
                  style={{ height: "300px" }}
                >
                  <RevenueOutstandingClient
                    chart_data={data?.revenue_outstanding_client}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-4 d-flex">
              <div className="card flex-fill" style={{ height: "400px" }}>
                <div className="card-header pb-2">
                  <h5 className="mb-0">Split by Team Member</h5>
                </div>
                <div
                  className="card-body d-flex flex-column p-2"
                  style={{ height: "300px" }}
                >
                  <RevenueOutstandingMember
                    chart_data={data?.revenue_outstanding_member}
                    outstandingData={options}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="card">
              <div className="card-body p-0">
                <div className="custom-datatable-filter table-responsive">
                  <div className="d-flex justify-content-end mb-1">
                    <button className="btn btn-sm btn-success" onClick={exportToExcel}>
                      <i className="fa fa-file-excel me-2"></i>
                      Export Excel
                    </button>
                  </div>
                  <div className="row p-1 mt-0">
                    {options.length > 0 && (
                      <MaterialReactTable
                        columns={columns}
                        data={options}
                        enableGrouping
                        initialState={{
                          density: "compact",
                          grouping: ["company_name"], // group by company_name
                          expanded: true, // expand groups by default
                          pagination: { pageSize: 100 },
                          columnOrder: [
                            "name",
                            "role_name",
                            "company_name",
                            "roleManager",
                            "assignedTo",
                            "bdBy",
                            "entity",
                            "status",
                            "invoice_number",
                            "raised_date",
                            "issue_date",
                            "due_date",
                            "issue_age_days",
                            "due_age_days",
                            "major_industry",
                            "region",
                            "amount",
                            "revenue",
                          ],
                          columnPinning: {
                            left: [], // 👈 no pinned columns
                            right: [],
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Outstanding;
